import { LoadingOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  Badge,
  Button,
  DatePicker,
  DatePickerProps,
  Form,
  Input,
  Progress,
  Radio,
  Select,
  Spin,
  Tooltip,
  message,
} from 'antd';
import { useForm } from 'antd/es/form/Form';
import dayjs from 'dayjs';
import { t } from 'i18next';
import fileDownload from 'js-file-download';
import React, { useEffect, useRef, useState } from 'react';
import ExtraTab from 'src/app/components/ExtraTabcomponent/ExtraTab';
import FormComponent from 'src/app/components/FormComponent/FormComponent';
import SearchableSelect from 'src/app/components/SearchableSelect/SearchableSelect';
import showModal from 'src/app/components/ShowModalComponent/ShowModal';
import { socketHelper } from 'src/app/helpers';
import { formatDate } from 'src/app/helpers/time.helper';

import { useNumberMutation, useStartMutation } from 'src/app/redux/blackSystem/blackSystem.slice';
import { useAppSelector } from 'src/app/redux/hook';
import { axios } from 'src/app/utils';
import { SENSOR_URL_LIST } from 'src/configs/system.config';
import { finalUrlsConfig } from 'src/configs/url.config';
import { BlackList } from 'src/enums/black-site.enum';
import { UploadIcon } from 'src/icons';

const now = dayjs();
const min = now.subtract(10, 'year');
const max = now.add(10, 'year');
function NewsApiComponent() {
  // States
  const [topicsArr, setTopicsArr] = useState<any[]>([]);
  const [submitType, setSubmitType] = useState<string>('');
  const [toDate, setToDate] = useState<string | undefined>();
  const [fromDate, setFromDate] = useState<string | undefined>();
  const [allProgress, setAllProgress] = useState<any[]>([]);
  const [eventProgress, setEventProgress] = useState<any>({});
  const [startProcess, setStartProcess] = useState<string>('');

  const [downloadState, setDownloadState] = useState<{
    state: boolean;
    size: string;
    done: boolean;
  }>({
    state: false,
    size: '0.00',
    done: false,
  });
  const [numberMessages, setNumberMessages] = useState<{
    message: string | number;
  }>({
    message: '',
  });
  const [zipCount, setZipCount] = useState<number>(0);
  // AntDState
  const [form] = useForm();
  const topic = Form.useWatch('addTopic', form);

  const selector = Form.useWatch('radioBtns', form);
  const site = Form.useWatch('site', form);
  const fromDateValue = Form.useWatch('fromDate', form);
  const toDateValue = Form.useWatch('toDate', form);

  const [isExportLoading, setIsExportLoading] = useState<boolean>(false);

  const [start] = useStartMutation();
  const [number, { isLoading: isNumberLoading }] = useNumberMutation();

  const { theme } = useAppSelector((state) => state.configContent);
  const ossArr = [BlackList.SELECT_SITE, ...SENSOR_URL_LIST().newsSiteUrls];
  const showNewsSite = site === BlackList.SELECT_SITE;

  const numberTimeoutRef: any = useRef(null);
  const exportTimeoutRef: any = useRef(null);

  const startProcessRef = useRef(startProcess);

  useEffect(() => {
    socketHelper.createConnection({ path: '/oss-progress' });
  }, []);

  useEffect(() => {
    startProcessRef.current = startProcess;
  }, [startProcess]);

  useEffect(() => {
    const socketData = socketHelper.listenSocket('event_progress', (data) => {
      if (startProcessRef.current === data.job.id) {
        setEventProgress(data.job);
      }
    });
  }, [socketHelper]);

  // The use effects below is to prevent users to send multiple requests to the backend if their inital request is still in pending.
  useEffect(() => {
    if (isNumberLoading) {
      numberTimeoutRef.current = setTimeout(() => {
        message.error(t('blackSystemPage.stillWaitingForNumberResponse'), 5);
      }, 5000);
    } else {
      clearTimeout(numberTimeoutRef.current);
    }

    return () => {
      clearTimeout(numberTimeoutRef.current);
    };
  }, [isNumberLoading]);

  useEffect(() => {
    if (isExportLoading) {
      exportTimeoutRef.current = setTimeout(() => {
        message.error(t('blackSystemPage.stillWaitingForExportResponse'), 5);
      }, 5000);
    } else {
      clearTimeout(exportTimeoutRef.current);
    }

    return () => {
      clearTimeout(exportTimeoutRef.current);
    };
  }, [isExportLoading]);

  const handleNumberReq = () => {
    if (isNumberLoading) return;
    setZipCount(0);
    setDownloadState({
      state: false,
      done: false,
      size: '0.00',
    });

    const url = showNewsSite ? 'newsApi' : 'OSS';
    let data = !showNewsSite ? { site } : { selector, topics: topicsArr };

    setNumberMessages({ message: '' });
    setZipCount(0);
    if ((site === BlackList.SELECT_SITE && !topicsArr.length) || !site) return;

    if (toDateValue && fromDateValue) {
      data = { ...data, ...(toDateValue && { toDate }), ...(fromDateValue && { fromDate }) };
    }
    number({ data, url })
      .unwrap()
      .then((res) => {
        setZipCount(res.number);
        if (res.oldest_folder_date) {
          setNumberMessages(() => ({
            message: `${res.number} ${t('blackSystemPage.newsApi.numberMessageCount')} ${res.oldest_folder_date}`,
          }));
        } else {
          setNumberMessages(() => {
            if (toDateValue && fromDateValue) {
              const formattedToDate = formatDate(toDateValue);
              const formattedFromDate = formatDate(fromDateValue);
              return { message: `${res.number}, between ${formattedToDate} & ${formattedFromDate}` };
            }
            return { message: `${res.number}` };
          });
        }
      })
      .catch(() => {
        setNumberMessages(() => ({
          message: '',
        }));
        setZipCount(0);
        message.error(t('blackSystemPage.numberOperationFailed'), 5);
      });
  };

  // If user adds a topic, or chenges the "search options", then the use effect,
  // Below refetches the number condition.
  useEffect(() => {
    handleNumberReq();
  }, [site, topicsArr.length, showNewsSite, toDateValue, fromDateValue]);

  const disabledDate = (current: dayjs.Dayjs, compareDate?: dayjs.Dayjs, isToDate?: boolean) => {
    const sevenYearsAgo = dayjs().subtract(7, 'year');

    if (isToDate && compareDate) {
      return current && (current.isAfter(dayjs().endOf('day')) || current.isBefore(compareDate));
    }

    if (current && current.isBefore(sevenYearsAgo, 'day')) {
      return true;
    }

    return current && current.isAfter(dayjs().endOf('day'));
  };

  const onFinishForm = async (url: string) => {
    const submitObj = { ...(toDateValue && { toDate }), ...(fromDateValue && { fromDate }) };
    const data =
      url === 'OSS'
        ? { ...submitObj, site }
        : {
            ...submitObj,
            topics: topicsArr,
            selector,
          };
    if (submitType === 'Start') {
      if (zipCount > 0) {
        showModal({
          title: t('blackSystemPage.startStartOperation'),
          content: <p>{t('blackSystemPage.startOperationWarnText')}</p>,
          onSave: (modal) => {
            start({ data: { ...data }, url })
              .unwrap()
              .then((payload) => {
                // console.log('payload.job_id zipcount>0', payload.job_id);
                setStartProcess(payload.job_id);
              })
              .catch(() => message.error(t('blackSystemPage.startOperationFailed')));
            modal.destroy();
            message.success(t('blackSystemPage.startOperationSuccess'), 5);
          },
          okText: t('blackSystemPage.startOkText'),
        });
      } else {
        message.success(t('blackSystemPage.startOperationSuccess'), 5);
        start({ data: { ...data }, url })
          .unwrap()
          .then((payload) => {
            // console.log('payload.job_id', payload.job_id);
            setStartProcess(payload.job_id);
          })
          .catch(() => message.error(t('blackSystemPage.startOperationFailed')));
      }
    } else if (submitType === 'Export') {
      try {
        setIsExportLoading(true);
        message.success(t('blackSystemPage.exportOperationSuccess'), 5);
        //  @ts-ignore
        await axios({
          url: finalUrlsConfig().blackSystem(url).export,
          method: 'POST',
          data,
          responseType: 'blob',
          onDownloadProgress: (progressEvent) => {
            // This section helps us to know the amount of data downloaded
            const bytesDownloaded = progressEvent.loaded;
            const megabytesDownloaded = bytesDownloaded / (1024 * 1024);
            if (megabytesDownloaded) {
              if (megabytesDownloaded > 1) {
                setIsExportLoading(false);
              }
              setDownloadState(() => ({
                state: true,
                size: `${megabytesDownloaded.toFixed(2)}`,
                done: false,
              }));
            }
          },
          blockSpinner: true,
        })
          .then(async (res: any) => {
            let fileName: string;
            const today = new Date();
            const formattedDate = formatDate(today);
            if (showNewsSite) {
              fileName = `${topicsArr.join('-')}-${formattedDate}`;
            } else {
              const splitedValue = site.split('/')[2];
              fileName = `${splitedValue}-${formattedDate}`;
            }
            // fileName = showNewsSite ? `${site}-${fromDate}` : `${topicsArr[0]}-${fromDate}`
            setDownloadState(() => ({ size: '0.00', state: false, done: true }));
            fileDownload(res.data, `${fileName}'s.zip`);
            setIsExportLoading(false);
          })
          .catch(() => {
            message.error(t('blackSystemPage.exportOperationFailed'), 5);
            setDownloadState(() => ({
              done: false,
              size: '0.00',
              state: false,
            }));
            setIsExportLoading(false);
          });
      } catch (error) {
        setIsExportLoading(false);
        console.log('err', error);
      }
    }
  };

  // Functions
  const handleAddTopic = (topicAdded: string) => {
    if (!topicsArr.includes(topicAdded) && topicAdded !== undefined && topicAdded !== '') {
      setTopicsArr((prevState) => [...prevState, topicAdded]);
      form.setFieldValue('addTopic', '');
    }
  };

  const handleDeleteTopic = (e: React.MouseEvent<HTMLButtonElement>) => {
    const x = e.currentTarget.value;
    setTopicsArr((prevState) => prevState.filter((eachTopic) => eachTopic !== x));
  };

  const handleAddTopicEnterKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.code === 'Enter') {
      handleAddTopic(topic);
    }
  };
  const onFromDateChange: DatePickerProps['onChange'] = (date, dateString) => {
    let formattedDate;
    if (typeof dateString === 'string') {
      formattedDate = dateString.split(' ');
      const secondsFromFormattedDate = formattedDate.pop();
      const newSeconds = `${secondsFromFormattedDate}:00`;
      formattedDate.push(newSeconds);
      formattedDate = formattedDate.join('T');
    }
    setFromDate(formattedDate);
  };

  const onToDateChange: DatePickerProps['onChange'] = (_date, dateString) => {
    let formattedDate;
    if (typeof dateString === 'string') {
      formattedDate = dateString.split(' ');
      const secondsFromFormattedDate = formattedDate.pop();
      const newSeconds = `${secondsFromFormattedDate}:00`;
      formattedDate.push(newSeconds);
      formattedDate = formattedDate.join('T');
    }
    setToDate(formattedDate);
  };

  const topicsList = topicsArr.map((eachTopic) => (
    <div
      key={eachTopic}
      className="topic-item"
      style={{
        ...(theme.colors.quoteBgc && {
          backgroundColor: theme.colors.quoteBgc,
          boxShadow: `2px 2px 2px ${theme.colors.quoteBgc}`,
        }),
      }}
    >
      <p>{eachTopic}</p>
      <button type="button" onClick={(e) => handleDeleteTopic(e)} value={eachTopic} className="delete-topic-btn">
        x
      </button>
    </div>
  ));

  // TODO: daha sonra permissionları ekle
  // const startPermission = checkPermission(permissions, [routes['/Portal'], 'OSSConfig', 'NewsSite', 'start']);
  // const exportPermission = checkPermission(permissions, [routes['/Portal'], 'OSSConfig', 'NewsSite', 'export']);
  // const generateNumberPermission = checkPermission(permissions, [
  //   routes['/Portal'],
  //   'OSSConfig',
  //   'NewsSite',
  //   'generateNumber',
  // ]);

  /*
      This section determines the button conditions, such as whether a button should be shown or not.
  */
  const disableConditions = ({ isExport, isStart }: { isExport?: boolean; isStart?: boolean }) => {
    if (isExport && (zipCount === 0 || !zipCount)) {
      return true;
    }
    if (showNewsSite) {
      if (isStart && !fromDateValue && !!toDateValue) {
        return true;
      }
      if ((isStart || isExport) && topicsArr.length === 0) {
        return true;
      }
    }
    if (isExport && isExportLoading) return true;
    return false;
  };
  const bottomPixelsForBtns = showNewsSite ? '-250px' : '-50px';

  const formType = showNewsSite ? 'newsApi' : 'OSS';
  return (
    <div className="social-media">
      <div className="first-form">
        <div className="news-api-form">
          <FormComponent
            extraButton={
              <div className="news-api-extra-btns" style={{ bottom: bottomPixelsForBtns }}>
                <ExtraTab
                  onClick={() => {
                    setSubmitType('Start');
                    form.submit();
                  }}
                  icon={null}
                  text={t('blackSystemPage.commonBtns.start')}
                  bgTheme
                  disabled={disableConditions({ isStart: true })}
                />
                <ExtraTab
                  onClick={() => {
                    setSubmitType('Export');
                    form.submit();
                  }}
                  icon={<UploadIcon />}
                  text={t('blackSystemPage.commonBtns.exportData')}
                  bgTheme
                  disabled={disableConditions({ isExport: true })}
                />
              </div>
            }
            options={{
              labelCol: { span: 9 },
              form,
              size: 'middle',
              onFinish: () => onFinishForm(formType),
            }}
          >
            <Form.Item
              name="site"
              label={
                <div style={{ display: 'flex' }}>
                  {t('blackSystemPage.OSS.selectSite')}
                  <Tooltip placement="topRight" title="Select Site">
                    <Badge offset={[10, 0]} />
                  </Tooltip>
                </div>
              }
              rules={[
                {
                  required: true,
                },
              ]}
              initialValue={ossArr[0]}
            >
              <SearchableSelect bordered={false}>
                {ossArr.map((sensor) => (
                  <Select.Option value={sensor} key={sensor}>
                    {sensor}
                  </Select.Option>
                ))}
              </SearchableSelect>
            </Form.Item>
            {showNewsSite && (
              <>
                <div className="topic">
                  <Form.Item
                    className="form-with-btn"
                    name="addTopic"
                    label={
                      <div style={{ display: 'flex' }}>
                        {t('blackSystemPage.newsApi.form.addNewKeyword')}
                        <Tooltip placement="topRight" title={t('AddTopic')}>
                          <Badge offset={[10, 0]} />
                        </Tooltip>
                      </div>
                    }
                    initialValue=""
                  >
                    <Input bordered={false} onKeyDown={(e) => handleAddTopicEnterKey(e)} type="text" />
                  </Form.Item>
                  <Button
                    onClick={() => handleAddTopic(topic)}
                    className="topic-add-btn"
                    style={{
                      ...(theme.colors.quoteBgc && {
                        backgroundColor: theme.colors.quoteBgc,
                      }),
                    }}
                  >
                    +
                  </Button>
                </div>
                <Form.Item
                  name="radioBtns"
                  label={
                    <div style={{ display: 'flex' }}>
                      {t('blackSystemPage.newsApi.form.chooseSelector')}
                      <Tooltip placement="topRight" title="Choose Selector">
                        <Badge offset={[10, 0]} />
                      </Tooltip>
                    </div>
                  }
                  rules={[
                    {
                      required: true,
                      message: t('errors.required'),
                    },
                  ]}
                  initialValue="and"
                >
                  <Radio.Group name="radioBtns">
                    <Radio value="and">{t('blackSystemPage.newsApi.and')}</Radio>
                    <Radio value="or">{t('blackSystemPage.newsApi.or')}</Radio>
                  </Radio.Group>
                </Form.Item>
              </>
            )}
            <Form.Item
              label={t('blackSystemPage.newsApi.form.fromDate')}
              htmlFor="fromDate"
              name="fromDate"
              rules={[{ required: !!toDateValue }]}
            >
              <DatePicker
                showTime
                variant="borderless"
                id="time"
                format="YYYY-MM-DD HH:mm"
                onChange={onFromDateChange}
                disabledDate={(current) => disabledDate(current)}
                showHour={showNewsSite}
                minDate={min}
                maxDate={max}
                showMinute={showNewsSite}
                showNow={false}
              />
            </Form.Item>
            <Form.Item label={t('blackSystemPage.newsApi.form.toDate')} htmlFor="toDate" name="toDate">
              <DatePicker
                showTime
                variant="borderless"
                id="time"
                format="YYYY-MM-DD HH:mm"
                onChange={onToDateChange}
                disabledDate={(current) => disabledDate(current, fromDateValue, true)}
                showHour={showNewsSite}
                minDate={min}
                maxDate={max}
                showMinute={showNewsSite}
                showNow={false}
              />
            </Form.Item>
          </FormComponent>
          {showNewsSite && topicsArr.length === 0 && <p>{t('blackSystemPage.atLeastOneTopicNeeded')}</p>}
          <div className="resultAreas">
            {(downloadState.done || downloadState.state) && (
              <div
                className="resultArea"
                style={{
                  ...(theme.colors.quoteBgc && {
                    borderTop: `1px solid ${theme.colors.quoteBgc}`,
                    borderBottom: `1px solid ${theme.colors.quoteBgc}`,
                    color: `${theme.colors.quoteBgc}`,
                  }),
                }}
              >
                {downloadState.state ? (
                  <>
                    <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
                    <p>{downloadState.size}mb</p>
                  </>
                ) : (
                  downloadState.done && (
                    <>
                      <Progress type="circle" percent={100} size={30} />
                      <p>{t('blackSystemPage.successfullyDownloaded')}</p>
                    </>
                  )
                )}
              </div>
            )}
            {numberMessages.message !== '' && !isNumberLoading && (
              <div className="resultAreas">
                <div
                  className="resultArea"
                  style={{
                    ...(theme.colors.quoteBgc && {
                      borderTop: `1px solid ${theme.colors.quoteBgc}`,
                      borderBottom: `1px solid ${theme.colors.quoteBgc}`,
                      color: `${theme.colors.quoteBgc}`,
                    }),
                  }}
                >
                  <Button
                    className="refetchBtn"
                    style={{ backgroundColor: `${theme.colors.quoteBgc}` }}
                    onClick={handleNumberReq}
                  >
                    {t('blackSystemPage.refetchBtn')} <ReloadOutlined style={{ fontSize: '1.25' }} />
                  </Button>
                  <p>
                    {t('blackSystemPage.newsApi.numberMessage')}
                    {numberMessages.message}
                  </p>
                </div>
                <Progress
                  size={80}
                  type="circle"
                  percent={eventProgress.progress}
                  strokeColor={eventProgress.progress === 100 ? 'green' : `${theme.colors.quoteBgc}`}
                />
              </div>
            )}
          </div>
        </div>
        {showNewsSite && (
          <div>
            {topicsArr.length > 0 ? (
              <div
                className="topics-area"
                style={{
                  ...(theme.colors.quoteBgc && {
                    border: `1px solid ${theme.colors.quoteBgc}`,
                  }),
                }}
              >
                {topicsList}
              </div>
            ) : (
              <div
                className="topics-area"
                style={{
                  ...(theme.colors.quoteBgc && {
                    border: `1px solid ${theme.colors.quoteBgc}`,
                  }),
                }}
              >
                <h1
                  style={{
                    marginLeft: '1em',
                    opacity: '0.3',
                    ...(theme.colors.quoteBgc && {
                      color: theme.colors.quoteBgc,
                    }),
                  }}
                >
                  {t('blackSystemPage.newsApi.form.noKeywordsAdded')}
                </h1>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default NewsApiComponent;
